import React from 'react';
import axios from 'axios';

/**
 * Msg #996 — Pokémon Stadium turn-based battle popup.
 *
 * Auto-shows when the user has an active or pending Stadium battle. Polls
 * /api/pokemon/stadium/active every 2s, and /api/pokemon/stadium/state during
 * a live battle. Renders a two-panel arena with HP bars, an animated attack
 * flash, a rolling log, and Attack / Special / Switch / Run buttons on your
 * turn. Winner banner + close button when finished.
 */
export const PokemonStadiumBattle = ({ apiBase, username, room, onClose }) => {
  const [battle, setBattle] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [flash, setFlash] = React.useState(0); // ticks up per log-line for animation
  const [inventoryPk, setInventoryPk] = React.useState([]); // for accept picker
  const [pickPokemon, setPickPokemon] = React.useState('');
  const [manuallyClosed, setManuallyClosed] = React.useState(false);

  const active = battle && (battle.status === 'pending' || battle.status === 'active');
  const isMine = battle && (
    battle.p1?.user?.toLowerCase() === (username || '').toLowerCase() ||
    battle.p2?.user?.toLowerCase() === (username || '').toLowerCase()
  );

  // Msg #1001 — Adaptive polling cadence.
  //   • Active known battle → 2 s (need snappy turn updates + HP anims).
  //   • Currently in Pokemon_Stadium → 4 s (looking for incoming challenges).
  //   • Elsewhere → 20 s (background check so a challenge posted while you
  //     roamed away still surfaces when you next look at the popup).
  React.useEffect(() => {
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      if (cancelled) return;
      try {
        if (battle && battle.battle_id) {
          const r = await axios.get(`${apiBase}/pokemon/stadium/state`, { params: { battle_id: battle.battle_id } });
          if (!cancelled) {
            const prevLen = (battle.log || []).length;
            const nextLen = (r.data?.log || []).length;
            if (nextLen > prevLen) setFlash((f) => f + 1);
            setBattle(r.data);
          }
        } else {
          const r = await axios.get(`${apiBase}/pokemon/stadium/active`, { params: { username } });
          if (!cancelled && r.data?.battle) setBattle(r.data.battle);
        }
      } catch { /* ignore transient */ }
      const isActiveBattle = battle && (battle.status === 'pending' || battle.status === 'active');
      const nextDelay = isActiveBattle
        ? 2000
        : (room === 'Pokemon_Stadium' ? 4000 : 20000);
      timer = setTimeout(tick, nextDelay);
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, username, room, battle?.battle_id, battle?.status]);

  // Load inventory when we need to pick a pokemon (accept phase).
  React.useEffect(() => {
    if (!battle || battle.status !== 'pending') return;
    if (battle.p2?.user?.toLowerCase() !== (username || '').toLowerCase()) return;
    axios.get(`${apiBase}/inventory`, { params: { username } })
      .then((r) => setInventoryPk(r.data?.pokemon || []))
      .catch(() => setInventoryPk([]));
  }, [apiBase, username, battle?.battle_id, battle?.status]);

  const doAction = async (action, extra = {}) => {
    if (!battle || !isMine) return;
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${apiBase}/pokemon/stadium/action`, {
        battle_id: battle.battle_id, actor: username, action, ...extra,
      });
      setBattle(r.data);
      setFlash((f) => f + 1);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Action failed.');
    } finally { setBusy(false); }
  };

  const accept = async () => {
    if (!battle || !pickPokemon) return;
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${apiBase}/pokemon/stadium/accept`, {
        battle_id: battle.battle_id, user: username, pokemon: pickPokemon,
      });
      setBattle(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Accept failed.');
    } finally { setBusy(false); }
  };

  if (!battle || !isMine) return null;
  if (manuallyClosed && battle.status === 'finished') return null;

  const myKey = battle.p1?.user?.toLowerCase() === (username || '').toLowerCase() ? 'p1' : 'p2';
  const oppKey = myKey === 'p1' ? 'p2' : 'p1';
  const me = battle[myKey] || {};
  const opp = battle[oppKey] || {};
  const myTurn = active && battle.status === 'active' &&
                 battle.turn?.toLowerCase() === (username || '').toLowerCase();

  const pct = (a, b) => (b > 0 ? Math.max(0, Math.min(100, Math.round((a / b) * 100))) : 0);

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1350,
  };
  const shell = {
    width: 'min(94vw, 640px)',
    background: 'linear-gradient(180deg, #dceffe 0%, #a7d6f5 60%, #e2f4d6 100%)',
    border: '4px double #b03030',
    boxShadow: '0 8px 30px rgba(0,0,0,0.5), inset 0 0 0 2px #fff, inset 0 0 0 3px #b03030',
    fontFamily: '"Press Start 2P", "Courier New", monospace',
    color: '#1a1a3a', padding: 0, overflow: 'hidden', position: 'relative',
  };
  const headerStrip = {
    background: '#b03030', color: '#fff',
    padding: '6px 12px', fontSize: 12, letterSpacing: 2,
    textTransform: 'uppercase', fontWeight: 'bold',
    textShadow: '0 1px 2px rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  };
  const hpBarWrap = (color) => ({
    width: '100%', height: 10, background: '#3a3a3a',
    border: '1px solid #000', overflow: 'hidden', marginTop: 4,
  });
  const hpBarFill = (v, color) => ({
    width: `${v}%`, height: '100%', background: color,
    transition: 'width 0.6s ease',
  });
  const fighterCard = (data, isFoe) => (
    <div
      data-testid={`stadium-fighter-${isFoe ? 'foe' : 'me'}`}
      style={{
        flex: 1, background: '#fff', border: '2px solid #000',
        padding: 10, minWidth: 0,
        fontFamily: 'Georgia, serif',
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 'bold', letterSpacing: 1, opacity: 0.8 }}>
        {isFoe ? '⚔ FOE' : '🎽 YOU'}
      </div>
      <div style={{ fontSize: 16, fontWeight: 'bold', margin: '2px 0' }}>
        {data?.pokemon || '—'}
      </div>
      <div style={{ fontSize: 11, opacity: 0.8 }}>{data?.user}</div>
      <div style={{ marginTop: 6, fontSize: 10 }}>
        HP {data?.hp ?? 0} / {data?.hp_max ?? 0}
      </div>
      <div style={hpBarWrap()}>
        <div style={hpBarFill(pct(data?.hp || 0, data?.hp_max || 1),
          (data?.hp || 0) / (data?.hp_max || 1) < 0.3 ? '#c0392b' :
          (data?.hp || 0) / (data?.hp_max || 1) < 0.6 ? '#e67e22' : '#2ecc71')} />
      </div>
    </div>
  );

  return (
    <div style={overlay} data-testid="stadium-battle-modal">
      <div style={shell}>
        <div style={headerStrip}>
          <span>🏟 Pokémon Stadium — Battle {battle.battle_id?.slice(-6)}</span>
          {(battle.status === 'finished') && (
            <button
              data-testid="stadium-close"
              onClick={() => { setManuallyClosed(true); if (onClose) onClose(); }}
              style={{
                background: '#fff', color: '#b03030', border: '1px solid #fff',
                padding: '2px 10px', fontWeight: 'bold', cursor: 'pointer',
              }}
            >CLOSE</button>
          )}
        </div>
        <div
          key={`flash-${flash}`}
          data-testid="stadium-arena"
          style={{
            padding: 14,
            display: 'flex', gap: 14, alignItems: 'stretch',
            animation: 'stadiumFlash 0.6s',
          }}
        >
          {fighterCard(opp, true)}
          <div style={{
            alignSelf: 'center', fontSize: 24, fontWeight: 'bold',
            color: '#b03030', textShadow: '0 1px 0 #fff',
          }}>VS</div>
          {fighterCard(me, false)}
        </div>

        <div
          data-testid="stadium-log"
          style={{
            margin: '0 14px', background: '#fff8e0',
            border: '2px solid #000', maxHeight: 130, overflowY: 'auto',
            fontFamily: 'Georgia, serif', fontSize: 12,
          }}
        >
          {(battle.log || []).slice(-40).map((line, i) => (
            <div key={i} style={{ padding: '3px 8px', borderBottom: '1px dashed #ddd' }}>{line}</div>
          ))}
        </div>

        {err && (
          <div style={{ padding: '4px 14px', color: '#7a1f1f', fontSize: 11 }}>{err}</div>
        )}

        {battle.status === 'pending' && myKey === 'p2' && (
          <div
            data-testid="stadium-accept-panel"
            style={{ padding: 14, background: 'rgba(255,255,255,0.6)' }}
          >
            <div style={{ marginBottom: 6, fontSize: 12, fontFamily: 'Georgia, serif' }}>
              Choose your Pokémon to accept the challenge:
            </div>
            <select
              data-testid="stadium-accept-pick"
              value={pickPokemon}
              onChange={(e) => setPickPokemon(e.target.value)}
              style={{
                padding: 4, fontSize: 12, marginRight: 6,
                border: '2px solid #000', minWidth: 160,
              }}
            >
              <option value="">— select —</option>
              {inventoryPk.map((p) => (
                <option key={p.npc} value={p.npc}>{p.nickname || p.npc}</option>
              ))}
            </select>
            <button
              data-testid="stadium-accept-btn"
              disabled={!pickPokemon || busy}
              onClick={accept}
              style={{
                background: '#2ecc71', color: '#fff', border: '2px solid #000',
                padding: '4px 12px', fontWeight: 'bold',
                cursor: (!pickPokemon || busy) ? 'wait' : 'pointer',
              }}
            >⚔ ACCEPT</button>
            <button
              data-testid="stadium-decline-btn"
              disabled={busy}
              onClick={() => doAction('run')}
              style={{
                marginLeft: 6,
                background: '#e74c3c', color: '#fff', border: '2px solid #000',
                padding: '4px 12px', fontWeight: 'bold',
                cursor: busy ? 'wait' : 'pointer',
              }}
            >🏃 DECLINE</button>
          </div>
        )}

        {battle.status === 'active' && (
          <div
            data-testid="stadium-actions"
            style={{
              padding: 12, background: 'rgba(255,255,255,0.6)',
              display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8,
            }}
          >
            {['attack', 'special', 'switch', 'run'].map((a) => {
              const disabled = !myTurn || busy;
              const color = a === 'attack' ? '#e67e22'
                : a === 'special' ? '#8e44ad'
                : a === 'switch' ? '#2980b9' : '#7f8c8d';
              return (
                <button
                  key={a}
                  data-testid={`stadium-action-${a}`}
                  disabled={disabled}
                  onClick={() => {
                    if (a === 'switch') {
                      const name = window.prompt('Switch to which Pokémon (name from your inventory)?');
                      if (name && name.trim()) doAction('switch', { switch_to: name.trim() });
                    } else if (a === 'run') {
                      if (window.confirm('Run from battle? Opponent wins by default.')) doAction('run');
                    } else {
                      doAction(a);
                    }
                  }}
                  style={{
                    background: disabled ? '#c0c0c0' : color,
                    color: '#fff', border: '2px solid #000',
                    padding: '10px 6px', fontSize: 12, fontWeight: 'bold',
                    textTransform: 'uppercase', letterSpacing: 1,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    fontFamily: '"Courier New", monospace',
                  }}
                  title={disabled ? "Wait for your turn" : `Use ${a}`}
                >
                  {a === 'attack' ? '⚔ ATTACK'
                   : a === 'special' ? '✨ SPECIAL'
                   : a === 'switch' ? '🔁 SWITCH'
                   : '🏃 RUN'}
                </button>
              );
            })}
            {!myTurn && (
              <div style={{
                gridColumn: '1 / -1', textAlign: 'center', fontSize: 11,
                fontFamily: 'Georgia, serif', color: '#3a3a3a',
              }}>Waiting for <b>{battle.turn}</b>…</div>
            )}
          </div>
        )}

        {battle.status === 'finished' && (
          <div
            data-testid="stadium-winner"
            style={{
              padding: 14, textAlign: 'center', fontSize: 16, fontWeight: 'bold',
              background: battle.winner?.toLowerCase() === (username || '').toLowerCase()
                ? '#2ecc71' : '#95a5a6',
              color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.5)',
              letterSpacing: 2, textTransform: 'uppercase',
            }}
          >
            🏆 Winner: {battle.winner} {battle.winner?.toLowerCase() === (username || '').toLowerCase() ? '(+1 kill!)' : ''}
          </div>
        )}
      </div>
      <style>{`@keyframes stadiumFlash {
        0% { transform: scale(1); }
        30% { transform: scale(1.02); filter: brightness(1.1); }
        100% { transform: scale(1); }
      }`}</style>
    </div>
  );
};

export default PokemonStadiumBattle;
